/**
 * Plugins and Connections are sections of Settings (`/settings?section=plugins|connections`).
 * Plugins has three views in a segmented control: the catalog, what is installed and the user's
 * own plugins; a plugin's page is a sub-page of the view it was opened from.
 */
export const PLUGIN_SECTIONS = [
  {
    id: 'browse',
    label: 'Browse',
    description: 'Apps, editors and services',
  },
  {
    id: 'installed',
    label: 'Installed',
    description: 'What your agents and automations use',
  },
  {
    id: 'custom',
    label: 'Custom',
    description: 'Your own plugins, MCP servers and skills',
  },
] as const;

export type PluginSection = (typeof PLUGIN_SECTIONS)[number]['id'];

function isPluginSection(value: string): value is PluginSection {
  return PLUGIN_SECTIONS.some((section) => section.id === value);
}

/** The view a `&view=` names; Browse when it names none. */
export function parsePluginSection(value: string | null): PluginSection {
  return value !== null && isPluginSection(value) ? value : 'browse';
}

/** Settings › Plugins, on one of its views (Browse needs no `&view=`). */
export function pluginsPath(section: PluginSection = 'browse'): string {
  return section === 'browse'
    ? '/settings?section=plugins'
    : `/settings?section=plugins&view=${section}`;
}

/** Where a plugin's page lives: its view, with the plugin in the query. */
export function pluginPagePath(section: PluginSection, name: string): string {
  return `${pluginsPath(section)}&plugin=${encodeURIComponent(name)}`;
}

/** Settings › Connections. */
export const CONNECTIONS_PATH = '/settings?section=connections';

/** Connections, on one account, its sign-in started (`&connection=`). */
export function connectionPagePath(connection: string): string {
  return `${CONNECTIONS_PATH}&connection=${encodeURIComponent(connection)}`;
}

/** The page of a service's plugin: its generated catalog entry, or the plugin about it. */
export function servicePluginPagePath(service: string): string {
  return pluginPagePath('browse', `service.${service}`);
}
